import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Order, OrderItem, Customer, Address, OrderDiscount } from '../types';
import { ShippingTier } from './shippingService';
import { ExchangeRates, Currency } from './currencyService';

export interface CreateOrderData {
  // Optional existing customer ID to link to
  existingCustomerId?: string;
  // Customer information
  customer: {
    name: string;
    email: string;
    phone?: string;
  };
  
  // Shipping address
  shippingAddress: {
    fullName: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phoneNumber: string;
  };
  
  // Order items
  items: Array<{
    id: string;
    name: string;
    price: number;
    onSalePrice?: number;
    onSale?: boolean;
    quantity: number;
    image: string;
    manufacturer?: string;
    hsCode?: string;
    originCountry?: string;
    listingQuantity?: number;
  }>;
  
  // Shipping configuration
  shipping: {
    tier: ShippingTier;
    cost: number;
    priorityProcessing: boolean;
    priorityFee: number;
    estimatedDelivery: Date;
  };
  
  // Currency and pricing
  currency: Currency;
  exchangeRates: ExchangeRates;
  subtotalUSD: number;
  shippingCostUSD: number;
  priorityFeeUSD: number;
  totalUSD: number;
  displayTotal: number; // Total in selected currency
  
  // Discount information (optional)
  discount?: {
    code: string;
    discountId: string;
    amount: number;
    type: 'percentage' | 'fixed' | 'free_shipping';
    amountUSD: number;
  };
  
  // Optional notes
  notes?: string;
  
  // Admin user who created the order
  createdBy?: string;
}

/**
 * Generate a unique order number
 */
const generateOrderNumber = (): string => {
  const timestamp = Date.now().toString();
  const random = Math.random().toString(36).substr(2, 4).toUpperCase();
  return `ADM-${timestamp.slice(-8)}-${random}`;
};

/**
 * Generate a unique customer ID
 */
const generateCustomerId = (): string => {
  return `cust-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Remove undefined values from object, replacing with null for Firestore compatibility
 */
const sanitizeDataForFirestore = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (obj instanceof Date || obj instanceof Timestamp) {
    return obj;
  }

  if (typeof obj === 'object' && obj._methodName === 'serverTimestamp') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeDataForFirestore(item));
  }
  
  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) {
        sanitized[key] = null;
      } else if (value !== null && typeof value === 'object') {
        sanitized[key] = sanitizeDataForFirestore(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
  
  return obj;
};

/**
 * Create a new order through admin portal
 */
export const createAdminOrder = async (orderData: CreateOrderData): Promise<string> => {
  try {
    console.log('Creating admin order with data:', orderData);
    
    // Generate unique IDs
    const orderNumber = generateOrderNumber();
    // Use existing customer ID if provided, otherwise generate new one
    const customerId = orderData.existingCustomerId || generateCustomerId();
    
    // Create shipping address
    const shippingAddress: Address = {
      id: `addr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      label: 'Shipping Address',
      fullName: orderData.shippingAddress.fullName,
      line1: orderData.shippingAddress.line1,
      line2: orderData.shippingAddress.line2 || '',
      city: orderData.shippingAddress.city,
      state: orderData.shippingAddress.state,
      zipCode: orderData.shippingAddress.zipCode,
      country: orderData.shippingAddress.country,
      phoneNumber: orderData.shippingAddress.phoneNumber,
      isDefault: true
    };
    
    // Create customer object
    const customer: Customer = {
      id: customerId,
      name: orderData.customer.name,
      email: orderData.customer.email,
      phone: orderData.customer.phone || '',
      addresses: [shippingAddress],
      address: {
        line1: orderData.shippingAddress.line1,
        line2: orderData.shippingAddress.line2,
        city: orderData.shippingAddress.city,
        state: orderData.shippingAddress.state,
        postal_code: orderData.shippingAddress.zipCode,
        country: orderData.shippingAddress.country
      },
      orderHistory: [],
      totalSpent: orderData.displayTotal,
      lastOrderDate: new Date(),
      notificationPreferences: {
        orderUpdates: true,
        promotions: false,
        newsletters: false
      },
      isActive: true,
      createdAt: new Date()
    };
    
    // Create order items with proper calculations
    const orderItems: OrderItem[] = orderData.items.map(item => {
      const effectivePrice = item.onSale && item.onSalePrice ? item.onSalePrice : item.price;
      const total = effectivePrice * item.quantity;
      
      return {
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        onSalePrice: item.onSalePrice,
        onSale: item.onSale || false,
        image: item.image,
        total: total,
        listingQuantity: item.listingQuantity || 1,
        manufacturer: item.manufacturer,
        hsCode: item.hsCode,
        originCountry: item.originCountry
      };
    });
    
    // Create discount object if provided
    const discount: OrderDiscount | null = orderData.discount ? {
      code: orderData.discount.code,
      discountId: orderData.discount.discountId,
      amount: orderData.discount.amountUSD, // Store in USD for consistency
      type: orderData.discount.type
    } : null;
    
    // Create the order object
    const order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'> = {
      orderNumber,
      userId: customerId, // Use customer ID as user ID for admin-created orders
      items: orderItems,
      customer,
      shipping: {
        address: shippingAddress,
        option: orderData.shipping.tier.id,
        method: orderData.shipping.tier.name, // Legacy field
        cost: orderData.shippingCostUSD, // Store in USD
        priorityProcessing: orderData.shipping.priorityProcessing,
        priorityFee: orderData.priorityFeeUSD, // Store in USD
        estimatedDelivery: orderData.shipping.estimatedDelivery
      },
      payment: {
        method: 'stripe', // Default to stripe for admin orders
        transactionId: `admin-${Date.now()}`, // Placeholder transaction ID
        amount: orderData.totalUSD, // Store in USD
        currency: 'USD', // Payment processing is in USD
        status: 'succeeded' // Admin orders are automatically marked as paid
      },
      status: 'paid', // Admin orders start as paid
      tracking: null, // Explicitly set optional fields to null
      orderTracking: null,
      refund: null,
      discount,
      easyPostLabel: null,
      subtotal: orderData.subtotalUSD, // Store in USD
      total: orderData.totalUSD, // Store in USD
      notes: orderData.notes || `Order created by admin${orderData.createdBy ? ` (${orderData.createdBy})` : ''}`,
      packed: false,
      inProcurement: false,
      procurementBatchId: null,
      // Currency metadata for accurate display
      purchaseCurrency: orderData.currency || null,
      exchangeRateSnapshot: orderData.exchangeRates ? (orderData.exchangeRates as unknown as { [key: string]: number }) : null,
      displayTotal: orderData.displayTotal || null
    };
    
    // Create the order document
    const ordersCollection = collection(db, 'orders');
    
    // Sanitize order data to remove undefined values
    const sanitizedOrder = sanitizeDataForFirestore({
      ...order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    const orderDocRef = await addDoc(ordersCollection, sanitizedOrder);
    
    // Update existing customer's order history and stats if linking to existing customer
    if (orderData.existingCustomerId) {
      try {
        const customerDoc = doc(db, 'users', orderData.existingCustomerId);
        const customerSnapshot = await getDoc(customerDoc);
        
        if (customerSnapshot.exists()) {
          const existingCustomer = customerSnapshot.data();
          const updatedOrderHistory = [...(existingCustomer.orderHistory || []), orderDocRef.id];
          const updatedTotalSpent = (existingCustomer.totalSpent || 0) + orderData.displayTotal;
          
          await updateDoc(customerDoc, {
            orderHistory: updatedOrderHistory,
            totalSpent: updatedTotalSpent,
            lastOrderDate: serverTimestamp(),
            orderCount: updatedOrderHistory.length,
            averageOrderValue: updatedTotalSpent / updatedOrderHistory.length,
            updatedAt: serverTimestamp()
          });
        }
      } catch (customerUpdateError) {
        console.error('Failed to update customer stats:', customerUpdateError);
        // Don't fail the order creation if customer update fails
      }
    }
    
    console.log('Admin order created successfully with ID:', orderDocRef.id);
    return orderDocRef.id;
    
  } catch (error) {
    console.error('Error creating admin order:', error);
    throw new Error(`Failed to create admin order: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Preview order totals without creating the order (for validation)
 */
export const previewOrderTotals = (orderData: Omit<CreateOrderData, 'createdBy'>): {
  subtotalUSD: number;
  shippingCostUSD: number;
  priorityFeeUSD: number;
  discountAmountUSD: number;
  totalUSD: number;
  displayTotal: number;
  breakdown: {
    subtotal: { usd: number; display: number };
    shipping: { usd: number; display: number };
    priority: { usd: number; display: number };
    discount: { usd: number; display: number };
    total: { usd: number; display: number };
  };
} => {
  // Calculate subtotal in USD
  const subtotalUSD = orderData.items.reduce((sum, item) => {
    const effectivePrice = item.onSale && item.onSalePrice ? item.onSalePrice : item.price;
    return sum + (effectivePrice * item.quantity);
  }, 0);
  
  // Get other costs in USD
  const shippingCostUSD = orderData.shipping.cost;
  const priorityFeeUSD = orderData.shipping.priorityProcessing ? orderData.shipping.priorityFee : 0;
  const discountAmountUSD = orderData.discount?.amountUSD || 0;
  
  // Calculate total in USD
  const totalUSD = subtotalUSD + shippingCostUSD + priorityFeeUSD - discountAmountUSD;
  
  // Convert to display currency
  const exchangeRate = orderData.exchangeRates[orderData.currency];
  
  const convertToDisplayCurrency = (usdAmount: number): number => {
    if (orderData.currency === 'USD') {
      return usdAmount;
    }
    // Apply ceiling rounding for non-USD currencies
    return Math.ceil(usdAmount * exchangeRate);
  };
  
  const displaySubtotal = convertToDisplayCurrency(subtotalUSD);
  const displayShipping = convertToDisplayCurrency(shippingCostUSD);
  const displayPriority = convertToDisplayCurrency(priorityFeeUSD);
  const displayDiscount = convertToDisplayCurrency(discountAmountUSD);
  const displayTotal = displaySubtotal + displayShipping + displayPriority - displayDiscount;
  
  return {
    subtotalUSD,
    shippingCostUSD,
    priorityFeeUSD,
    discountAmountUSD,
    totalUSD,
    displayTotal,
    breakdown: {
      subtotal: { usd: subtotalUSD, display: displaySubtotal },
      shipping: { usd: shippingCostUSD, display: displayShipping },
      priority: { usd: priorityFeeUSD, display: displayPriority },
      discount: { usd: discountAmountUSD, display: displayDiscount },
      total: { usd: totalUSD, display: displayTotal }
    }
  };
};

/**
 * Validate order data before creation
 */
export const validateOrderData = (orderData: CreateOrderData): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Validate customer information
  if (!orderData.customer.name?.trim()) {
    errors.push('Customer name is required');
  }
  
  if (!orderData.customer.email?.trim() || !orderData.customer.email.includes('@')) {
    errors.push('Valid customer email is required');
  }
  
  // Validate shipping address
  const addr = orderData.shippingAddress;
  if (!addr.fullName?.trim()) errors.push('Shipping address full name is required');
  if (!addr.line1?.trim()) errors.push('Shipping address line 1 is required');
  if (!addr.city?.trim()) errors.push('Shipping address city is required');
  if (!addr.state?.trim()) errors.push('Shipping address state is required');
  if (!addr.zipCode?.trim()) errors.push('Shipping address zip code is required');
  if (!addr.country?.trim()) errors.push('Shipping address country is required');
  if (!addr.phoneNumber?.trim()) errors.push('Shipping address phone number is required');
  
  // Validate items
  if (!orderData.items || orderData.items.length === 0) {
    errors.push('At least one item is required');
  } else {
    orderData.items.forEach((item, index) => {
      if (!item.id?.trim()) errors.push(`Item ${index + 1}: Product ID is required`);
      if (!item.name?.trim()) errors.push(`Item ${index + 1}: Product name is required`);
      if (!item.image?.trim()) errors.push(`Item ${index + 1}: Product image is required`);
      if (typeof item.price !== 'number' || item.price <= 0) {
        errors.push(`Item ${index + 1}: Valid price is required`);
      }
      if (typeof item.quantity !== 'number' || item.quantity <= 0) {
        errors.push(`Item ${index + 1}: Valid quantity is required`);
      }
    });
  }
  
  // Validate shipping
  if (!orderData.shipping.tier) {
    errors.push('Shipping tier is required');
  }
  
  if (typeof orderData.shipping.cost !== 'number' || orderData.shipping.cost < 0) {
    errors.push('Valid shipping cost is required');
  }
  
  // Validate currency and totals
  if (!orderData.currency || !['USD', 'CAD', 'GBP'].includes(orderData.currency)) {
    errors.push('Valid currency is required');
  }
  
  if (!orderData.exchangeRates || typeof orderData.exchangeRates !== 'object') {
    errors.push('Exchange rates are required');
  }
  
  if (typeof orderData.totalUSD !== 'number' || orderData.totalUSD <= 0) {
    errors.push('Valid total amount is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
